import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getWeChatLoginConfigApi, getWeChatLoginUrl } from '../utils/authApi';

const phoneRegex = /^1[3-9]\d{9}$/;

const Login = ({ onSwitchToRegister, onClose }) => {
  const [formData, setFormData] = useState({
    phoneNumber: '',
    verificationCode: '',
    username: '',
  });
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [wechatLoading, setWechatLoading] = useState(false);
  const [wechatConfig, setWechatConfig] = useState({ enabled: false, checked: false });
  const [needsUsername, setNeedsUsername] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const countdownTimer = useRef(null);

  const { smsLogin, sendVerificationCode, useServerAuth } = useAuth();

  useEffect(() => {
    return () => {
      if (countdownTimer.current) {
        clearInterval(countdownTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadWeChatConfig = async () => {
      if (!useServerAuth) {
        setWechatConfig({ enabled: false, checked: true });
        return;
      }

      const result = await getWeChatLoginConfigApi();
      if (!cancelled) {
        setWechatConfig({
          enabled: result.success && result.enabled,
          checked: true,
          message: result.message,
        });
      }
    };

    loadWeChatConfig();
    return () => {
      cancelled = true;
    };
  }, [useServerAuth]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess('');
  };

  const startCountdown = () => {
    setCountdown(60);
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
    }
    countdownTimer.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownTimer.current);
          countdownTimer.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    if (!formData.phoneNumber) {
      setError('请先输入手机号');
      return;
    }
    if (!phoneRegex.test(formData.phoneNumber)) {
      setError('请输入正确的手机号格式');
      return;
    }

    setSendingCode(true);
    setError('');
    setSuccess('');

    try {
      const result = await sendVerificationCode(formData.phoneNumber);
      if (result.success) {
        setSuccess('验证码已发送，请查收短信');
        startCountdown();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('发送验证码失败，请稍后重试');
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!formData.phoneNumber || !formData.verificationCode) {
      setError('请输入手机号和验证码');
      setLoading(false);
      return;
    }
    if (!phoneRegex.test(formData.phoneNumber)) {
      setError('请输入正确的手机号格式');
      setLoading(false);
      return;
    }
    if (needsUsername && !formData.username.trim()) {
      setError('请先设置用户名');
      setLoading(false);
      return;
    }

    try {
      const result = await smsLogin({
        phoneNumber: formData.phoneNumber,
        verificationCode: formData.verificationCode,
        username: formData.username.trim(),
      });

      if (result.success) {
        onClose();
      } else if (result.needsUsername) {
        setNeedsUsername(true);
        setError('首次登录请先设置用户名');
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('登录失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleWeChatLogin = () => {
    if (!wechatConfig.enabled) {
      setError('微信登录暂未配置，请先使用手机号登录');
      return;
    }

    setWechatLoading(true);
    const nextPath = `${window.location.pathname}${window.location.search}`;
    window.location.href = getWeChatLoginUrl(nextPath);
  };

  return (
    <div className="w-full">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-sage-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
        </div>
        <h2 className="text-3xl font-serif font-bold text-sage-800 mb-2">手机号登录</h2>
        <p className="text-sage-600">输入验证码即可进入社区</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="phoneNumber" className="block text-base font-semibold text-sage-700 mb-3">
            手机号 <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-6 w-6 text-sage-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="请输入11位手机号"
              className="w-full pl-12 pr-4 py-4 text-lg border-2 border-sage-300 rounded-xl focus:outline-none focus:ring-3 focus:ring-sage-500 focus:border-sage-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
              maxLength="11"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="tel"
            />
          </div>
        </div>

        <div>
          <label htmlFor="verificationCode" className="block text-base font-semibold text-sage-700 mb-3">
            验证码 <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-6 w-6 text-sage-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <input
                type="text"
                id="verificationCode"
                name="verificationCode"
                value={formData.verificationCode}
                onChange={handleChange}
                placeholder="6位验证码"
                className="w-full pl-12 pr-4 py-4 text-lg tracking-widest border-2 border-sage-300 rounded-xl focus:outline-none focus:ring-3 focus:ring-sage-500 focus:border-sage-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                maxLength="6"
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            </div>
            <button
              type="button"
              onClick={handleSendCode}
              disabled={sendingCode || countdown > 0}
              className="shrink-0 w-full sm:w-auto px-5 py-4 bg-sage-600 text-white rounded-xl hover:bg-sage-700 focus:outline-none focus:ring-3 focus:ring-sage-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold shadow-md"
            >
              {sendingCode ? '发送中' : countdown > 0 ? `${countdown}s 后重发` : '获取验证码'}
            </button>
          </div>
        </div>

        {needsUsername && (
          <div>
            <label htmlFor="username" className="block text-base font-semibold text-sage-700 mb-3">
              用户名 <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-6 w-6 text-sage-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="设置社区显示用户名"
                className="w-full pl-12 pr-4 py-4 text-lg border-2 border-sage-300 rounded-xl focus:outline-none focus:ring-3 focus:ring-sage-500 focus:border-sage-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                maxLength="20"
              />
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-4 py-3 rounded-r-lg text-sm flex items-center">
            <svg className="w-5 h-5 mr-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-400 text-green-700 px-4 py-3 rounded-r-lg text-sm flex items-center">
            <svg className="w-5 h-5 mr-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {success}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-sage-600 to-emerald-600 text-white py-3 px-6 rounded-lg hover:from-sage-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-sage-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              登录中...
            </div>
          ) : needsUsername ? '设置并登录' : '立即登录'}
        </button>
      </form>

      {useServerAuth && (
        <div className="mt-6">
          <div className="relative mb-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-sage-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-sage-500">或</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleWeChatLogin}
            disabled={wechatLoading || !wechatConfig.checked}
            className="w-full flex items-center justify-center gap-3 border-2 border-green-500 text-green-700 bg-white py-3 px-6 rounded-lg hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold shadow-sm hover:shadow-md"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500 text-sm font-bold text-white">
              微
            </span>
            {wechatLoading ? '正在跳转...' : '微信登录'}
          </button>

          {wechatConfig.checked && !wechatConfig.enabled && (
            <p className="mt-2 text-center text-sm text-sage-500">
              微信登录暂未配置
            </p>
          )}
        </div>
      )}

      <div className="mt-8 text-center">
        <button
          onClick={onSwitchToRegister}
          className="text-sage-600 hover:text-sage-800 font-semibold transition-colors duration-200 hover:underline"
        >
          使用密码注册
        </button>
      </div>
    </div>
  );
};

export default Login;
